import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Trophy, Award, Star, Zap, Target, TrendingUp, 
  Download, Sparkles, Medal, Crown, Book, Calendar
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Badge category colors
const categoryColors = {
  points: { bg: 'from-amber-400 to-yellow-500', border: 'border-amber-300', text: 'text-amber-700' },
  behavior: { bg: 'from-emerald-400 to-green-500', border: 'border-emerald-300', text: 'text-emerald-700' },
  streak: { bg: 'from-orange-400 to-red-500', border: 'border-orange-300', text: 'text-orange-700' },
  activity: { bg: 'from-blue-400 to-indigo-500', border: 'border-blue-300', text: 'text-blue-700' },
  reading: { bg: 'from-purple-400 to-pink-500', border: 'border-purple-300', text: 'text-purple-700' },
  achievement: { bg: 'from-rose-400 to-pink-500', border: 'border-rose-300', text: 'text-rose-700' },
  special: { bg: 'from-cyan-400 to-teal-500', border: 'border-cyan-300', text: 'text-cyan-700' },
};

// All possible badges (for showing locked ones)
const ALL_BADGES = [
  { type: "10_points", name: "Бастаушы", icon: "🎯", description: "Алғашқы 10 ұпай", category: "points" },
  { type: "25_points", name: "25 Ұпай", icon: "🌟", description: "25+ ұпай жинады", category: "points" },
  { type: "50_points", name: "50 Ұпай", icon: "⭐", description: "50+ ұпай жинады", category: "points" },
  { type: "100_club", name: "100 Клубы", icon: "💯", description: "100+ ұпай жинады", category: "points" },
  { type: "200_club", name: "200 Клубы", icon: "💎", description: "200+ ұпай жинады", category: "points" },
  { type: "perfect", name: "Мінсіз", icon: "✨", description: "Ескертусіз ай", category: "behavior" },
  { type: "homework_hero", name: "Үй тапсырма батыры", icon: "📝", description: "10+ үй тапсырма", category: "behavior" },
  { type: "polite", name: "Әдепті", icon: "🎀", description: "5+ мәдениетті мінез-құлық", category: "behavior" },
  { type: "punctual", name: "Уақыттылық", icon: "⏰", description: "10+ уақытында келу", category: "behavior" },
  { type: "fire_streak", name: "Өрт сериясы", icon: "🔥", description: "5 күн қатарынан мақтау", category: "streak" },
  { type: "streak_7", name: "Апта сериясы", icon: "📆", description: "7 күн қатарынан мақтау", category: "streak" },
  { type: "streak_30", name: "Ай сериясы", icon: "🌙", description: "30 күн қатарынан мақтау", category: "streak" },
  { type: "active", name: "Белсенді", icon: "⚡", description: "10+ жазба", category: "activity" },
  { type: "super_active", name: "Супер белсенді", icon: "🌪️", description: "25+ жазба", category: "activity" },
  { type: "first_book", name: "Бірінші кітап", icon: "📚", description: "Бірінші кітапты оқыды", category: "reading" },
  { type: "bookworm", name: "Кітапқұмар", icon: "📖", description: "3+ кітап оқыды", category: "reading" },
  { type: "library_star", name: "Кітапхана жұлдызы", icon: "🏛️", description: "5+ кітап оқыды", category: "reading" },
  { type: "reader_master", name: "Оқу шебері", icon: "📕", description: "10+ кітап оқыды", category: "reading" },
  { type: "class_leader", name: "Сынып көшбасшысы", icon: "🎖️", description: "Сыныптағы үздік", category: "achievement" },
  { type: "weekly_star", name: "Апта жұлдызы", icon: "🥇", description: "Апталық үздік оқушы", category: "achievement" },
];

export default function GamificationPanel({ badgesData, studentId }) {
  const { getAuthHeaders } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  if (!badgesData) return null;

  const { badges = [], level, total_points, merit_points, book_chats_completed = 0 } = badgesData;
  const earnedBadgeTypes = badges.map(b => b.type);

  // Merge earned badges with all possible badges
  const allBadgesWithStatus = ALL_BADGES.map(badge => {
    const earned = badges.find(b => b.type === badge.type);
    return earned ? { ...badge, ...earned, earned: true } : { ...badge, earned: false };
  });

  // Group badges by category
  const badgesByCategory = {
    points: allBadgesWithStatus.filter(b => b.category === 'points'),
    behavior: allBadgesWithStatus.filter(b => b.category === 'behavior'),
    streak: allBadgesWithStatus.filter(b => b.category === 'streak'),
    activity: allBadgesWithStatus.filter(b => b.category === 'activity'),
    reading: allBadgesWithStatus.filter(b => b.category === 'reading'),
    achievement: allBadgesWithStatus.filter(b => b.category === 'achievement'),
  };

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/students/${studentId}/certificate`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${studentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Сертификат жүктелді!');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Сертификатты жүктеу сәтсіз болды');
    } finally {
      setDownloading(false);
    }
  };

  const earnedCount = badges.length;
  const totalBadges = ALL_BADGES.length;
  const completionPercentage = Math.round((earnedCount / totalBadges) * 100);

  return (
    <Card className="paper-card border-0 shadow-xl overflow-hidden">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Trophy className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Ойынластыру</h2>
              <p className="text-white/80 text-sm">Марапаттар, деңгейлер және жетістіктер</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={downloadCertificate}
            disabled={downloading || earnedCount === 0}
            className="bg-white/20 border-white/30 text-white hover:bg-white/30"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? 'Жүктелуде...' : 'Сертификат'}
          </Button>
        </div>
      </div>

      <CardContent className="p-6">
        <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <Star className="w-4 h-4" />
              Жалпы
            </TabsTrigger>
            <TabsTrigger value="badges" className="gap-2">
              <Medal className="w-4 h-4" />
              Марапаттар
            </TabsTrigger>
            <TabsTrigger value="level" className="gap-2">
              <Crown className="w-4 h-4" />
              Деңгей
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-4 text-center border-2 border-purple-300 shadow-sm">
                <div className="text-3xl font-bold text-purple-800">{level?.current?.level || 1}</div>
                <p className="text-sm font-medium text-purple-900 mt-1">Деңгей</p>
              </div>
              <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl p-4 text-center border-2 border-amber-300 shadow-sm">
                <div className="text-3xl font-bold text-amber-800">{earnedCount}</div>
                <p className="text-sm font-medium text-amber-900 mt-1">Марапат</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl p-4 text-center border-2 border-emerald-300 shadow-sm">
                <div className="text-3xl font-bold text-emerald-800">{merit_points || 0}</div>
                <p className="text-sm font-medium text-emerald-900 mt-1">Ұпай</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-4 text-center border-2 border-blue-300 shadow-sm">
                <div className="text-3xl font-bold text-blue-800">{book_chats_completed}</div>
                <p className="text-sm font-medium text-blue-900 mt-1">Кітап</p>
              </div>
            </div>

            {/* Level Progress */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-5 border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{level?.current?.icon || '🌱'}</span>
                  <div>
                    <h3 className="font-bold text-lg">{level?.current?.name || 'Бастаушы'}</h3>
                    <p className="text-xs text-muted">Деңгей {level?.current?.level || 1}</p>
                  </div>
                </div>
                {level?.next && (
                  <div className="text-right">
                    <span className="text-2xl">{level.next.icon}</span>
                    <p className="text-xs text-muted">{level.next.name}</p>
                  </div>
                )}
              </div>
              <Progress value={level?.progress || 0} className="h-3 mb-2" />
              <div className="flex justify-between text-xs text-muted">
                <span>{merit_points || 0} ұпай</span>
                {level?.next && <span>Келесі: {level.next.min_points} ұпай</span>}
              </div>
            </div>

            {/* Recent Badges */}
            {badges.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  Соңғы марапаттар
                </h3>
                <div className="flex flex-wrap gap-3">
                  {badges.slice(0, 6).map((badge, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-lg hover:scale-105 transition-transform cursor-default"
                      style={{
                        background: badge.category === 'points' ? 'linear-gradient(135deg, #b45309, #d97706)' :
                                   badge.category === 'behavior' ? 'linear-gradient(135deg, #047857, #059669)' :
                                   badge.category === 'streak' ? 'linear-gradient(135deg, #c2410c, #ea580c)' :
                                   badge.category === 'activity' ? 'linear-gradient(135deg, #1d4ed8, #2563eb)' :
                                   badge.category === 'reading' ? 'linear-gradient(135deg, #7c3aed, #8b5cf6)' :
                                   'linear-gradient(135deg, #be185d, #db2777)',
                      }}
                    >
                      <span className="text-xl drop-shadow-sm">{badge.icon}</span>
                      <span className="text-sm font-semibold text-white drop-shadow-sm">{badge.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Collection Progress */}
            <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-700">Жалпы жинау</span>
                <span className="text-sm font-bold text-slate-800">{earnedCount}/{totalBadges}</span>
              </div>
              <Progress value={completionPercentage} className="h-2.5" />
              <p className="text-xs font-medium text-slate-600 mt-2 text-center">
                {completionPercentage}% жиналды — {totalBadges - earnedCount} марапат қалды!
              </p>
            </div>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges" className="space-y-6">
            {Object.entries(badgesByCategory).map(([category, categoryBadges]) => (
              <div key={category}>
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-slate-800">
                  {category === 'points' && <Target className="w-4 h-4 text-amber-600" />}
                  {category === 'behavior' && <Award className="w-4 h-4 text-emerald-600" />}
                  {category === 'streak' && <Zap className="w-4 h-4 text-orange-600" />}
                  {category === 'activity' && <TrendingUp className="w-4 h-4 text-blue-600" />}
                  {category === 'reading' && <Book className="w-4 h-4 text-purple-600" />}
                  {category === 'achievement' && <Trophy className="w-4 h-4 text-rose-600" />}
                  {{
                    points: 'Ұпай негізінде',
                    behavior: 'Мінез-құлық',
                    streak: 'Серия',
                    activity: 'Белсенділік',
                    reading: 'Кітап оқу',
                    achievement: 'Жетістіктер'
                  }[category]}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {categoryBadges.map((badge, index) => {
                    const earnedBg = {
                      points: 'linear-gradient(135deg, #b45309, #d97706)',
                      behavior: 'linear-gradient(135deg, #047857, #059669)',
                      streak: 'linear-gradient(135deg, #c2410c, #ea580c)',
                      activity: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                      reading: 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
                      achievement: 'linear-gradient(135deg, #be185d, #db2777)',
                    };
                    
                    return (
                      <div 
                        key={index}
                        className={`relative p-4 rounded-xl text-center transition-all duration-300 ${
                          badge.earned 
                            ? 'shadow-lg hover:scale-105 cursor-default'
                            : 'bg-slate-100 border border-slate-200'
                        }`}
                        style={badge.earned ? { background: earnedBg[badge.category] || earnedBg.points } : {}}
                      >
                        <div className={`text-3xl mb-2 ${!badge.earned && 'grayscale opacity-40'}`}>
                          {badge.icon}
                        </div>
                        <p className={`font-bold text-xs ${badge.earned ? 'text-white drop-shadow-sm' : 'text-slate-500'}`}>
                          {badge.name}
                        </p>
                        <p className={`text-[10px] mt-1 ${badge.earned ? 'text-white/90' : 'text-slate-400'}`}>
                          {badge.description}
                        </p>
                        {badge.earned && (
                          <div className="absolute top-1.5 right-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-white drop-shadow-sm" />
                          </div>
                        )}
                        {!badge.earned && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-xl">
                            <span className="text-2xl opacity-30">🔒</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Level Tab */}
          <TabsContent value="level" className="space-y-6">
            {/* Current Level Display */}
            <div className="text-center py-6">
              <div 
                className="inline-flex items-center justify-center w-32 h-32 rounded-full shadow-xl mb-4"
                style={{ 
                  background: `linear-gradient(135deg, ${level?.current?.color || '#94a3b8'}, ${level?.current?.color || '#94a3b8'}88)` 
                }}
              >
                <div className="text-center text-white">
                  <div className="text-5xl mb-1">{level?.current?.icon || '🌱'}</div>
                  <div className="text-xl font-bold">{level?.current?.level || 1}</div>
                </div>
              </div>
              <h3 className="text-2xl font-bold">{level?.current?.name || 'Бастаушы'}</h3>
              <p className="text-muted">Деңгей {level?.current?.level || 1}</p>
            </div>

            {/* Level Progress */}
            <div className="bg-slate-50 rounded-xl p-5">
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium">Келесі деңгейге дейін</span>
                <span className="text-sm text-muted">{level?.progress || 0}%</span>
              </div>
              <Progress value={level?.progress || 0} className="h-4 mb-3" />
              <div className="flex justify-between text-sm">
                <span className="text-emerald-600 font-medium">{merit_points || 0} ұпай</span>
                {level?.next && (
                  <span className="text-muted">Келесі: {level.next.min_points} ұпай</span>
                )}
              </div>
            </div>

            {/* All Levels List */}
            <div>
              <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Барлық деңгейлер
              </h3>
              <div className="space-y-3">
                {[
                  { level: 1, name: "Бастаушы", min_points: 0, icon: "🌱", color: "#94a3b8" },
                  { level: 2, name: "Оқушы", min_points: 20, icon: "📚", color: "#60a5fa" },
                  { level: 3, name: "Жетістік", min_points: 50, icon: "⭐", color: "#a78bfa" },
                  { level: 4, name: "Жұлдыз", min_points: 100, icon: "🌟", color: "#fbbf24" },
                  { level: 5, name: "Супер Жұлдыз", min_points: 200, icon: "💫", color: "#f97316" },
                  { level: 6, name: "Чемпион", min_points: 350, icon: "🏆", color: "#ef4444" },
                  { level: 7, name: "Аңыз", min_points: 500, icon: "👑", color: "#ec4899" },
                ].map((lvl) => {
                  const isCurrentOrPast = (merit_points || 0) >= lvl.min_points;
                  const isCurrent = level?.current?.level === lvl.level;
                  
                  return (
                    <div 
                      key={lvl.level}
                      className={`flex items-center gap-4 p-3 rounded-xl transition-all ${
                        isCurrent 
                          ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 shadow-md' 
                          : isCurrentOrPast 
                            ? 'bg-emerald-50 border border-emerald-200'
                            : 'bg-gray-50 border border-gray-200 opacity-60'
                      }`}
                    >
                      <div 
                        className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                          isCurrentOrPast ? '' : 'grayscale'
                        }`}
                        style={{ backgroundColor: isCurrentOrPast ? `${lvl.color}30` : '#e5e7eb' }}
                      >
                        {lvl.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${isCurrent ? 'text-purple-700' : ''}`}>
                            {lvl.name}
                          </span>
                          {isCurrent && (
                            <Badge variant="secondary" className="bg-purple-200 text-purple-700 text-[10px]">
                              Қазір
                            </Badge>
                          )}
                          {isCurrentOrPast && !isCurrent && (
                            <span className="text-emerald-500 text-xs">✓</span>
                          )}
                        </div>
                        <p className="text-xs text-muted">{lvl.min_points} ұпай қажет</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-lg font-bold ${isCurrentOrPast ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {lvl.level}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
